<div class="w-full max-w-[860px] max-h-[86vh] flex flex-col bg-white rounded-2xl overflow-hidden">

  <!-- Header -->
  <div class="px-6 pt-5 pb-4 border-b border-gray-100 flex items-start gap-3">
    <span class="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
      <mat-icon class="!text-amber-600">upload_file</mat-icon>
    </span>
    <div class="min-w-0 flex-1">
      <h2 class="text-lg font-bold text-gray-900">{{ done ? 'Import finished' : 'Check the changes before updating' }}</h2>
      <p class="text-sm text-gray-500 truncate">{{ data.fileName }} · {{ plan.rowsRead | number }} row{{ plan.rowsRead === 1 ? '' : 's' }} read</p>
    </div>
    <button class="h-9 w-9 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center disabled:opacity-40" [disabled]="applying" (click)="close()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">

    <!-- Result -->
    @if (done) {
      <div class="rounded-xl px-4 py-3 text-sm flex items-start gap-2"
        [class]="failures.length ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'bg-emerald-50 border border-emerald-200 text-emerald-900'">
        <mat-icon class="!text-[20px] !h-5 !w-5 flex-shrink-0">{{ failures.length ? 'warning' : 'check_circle' }}</mat-icon>
        <span><b>{{ saved }}</b> item{{ saved === 1 ? '' : 's' }} updated{{ failures.length ? ', ' + failures.length + ' could not be saved (below)' : '' }}. Stock changes are in each item's history as "Imported from spreadsheet".</span>
      </div>
      @if (failures.length) {
      <ul class="text-sm divide-y divide-gray-100 border border-gray-100 rounded-xl">
        @for (f of failures; track f.update.id) {
        <li class="px-4 py-2"><span class="font-semibold text-gray-900">{{ f.update.name }}</span>@if (f.update.variant) { <span class="text-gray-500"> · {{ f.update.variant }}</span> } <span class="text-red-700"> - {{ f.message }}</span></li>
        }
      </ul>
      }
    } @else {

    <!-- Summary -->
    <div class="grid grid-cols-3 gap-3">
      <div class="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
        <div class="text-2xl font-bold text-gray-900 leading-none">{{ plan.updates.length | number }}</div>
        <div class="text-xs text-gray-600 mt-1">item{{ plan.updates.length === 1 ? '' : 's' }} to update ({{ changeCount }} change{{ changeCount === 1 ? '' : 's' }})</div>
      </div>
      <div class="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
        <div class="text-2xl font-bold text-gray-900 leading-none">{{ plan.unchanged | number }}</div>
        <div class="text-xs text-gray-600 mt-1">unchanged</div>
      </div>
      <div class="rounded-xl px-4 py-3 border" [class]="plan.problems.length ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'">
        <div class="text-2xl font-bold leading-none" [class]="plan.problems.length ? 'text-red-700' : 'text-gray-900'">{{ plan.problems.length | number }}</div>
        <div class="text-xs text-gray-600 mt-1">skipped (problems)</div>
      </div>
    </div>

    <p class="text-xs text-gray-500">
      Updating: <b class="text-gray-700">{{ plan.applied.join(', ') || 'nothing' }}</b>.
      @if (plan.ignored.length) { Not imported (left as they are): {{ plan.ignored.join(', ') }}. }
      Blank stock or price cells are left as they are; a blank discount means no discount.
    </p>

    <!-- Problems -->
    @if (plan.problems.length) {
    <div class="rounded-xl border border-red-100">
      <div class="px-4 py-2 bg-red-50 text-sm font-semibold text-red-800 rounded-t-xl flex items-center gap-1.5">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">error_outline</mat-icon> These rows will be skipped - fix them in the sheet and import again if needed
      </div>
      <ul class="text-sm divide-y divide-red-50">
        @for (p of problemsShown; track $index) {
        <li class="px-4 py-1.5"><span class="text-gray-500">Row {{ p.row }}</span>@if (p.name) { <span class="font-medium text-gray-800"> · {{ p.name }}</span> } <span class="text-red-700"> - {{ p.message }}</span></li>
        }
      </ul>
      @if (plan.problems.length > 8) {
      <button class="w-full text-xs font-semibold text-red-700 py-1.5 hover:bg-red-50 rounded-b-xl" (click)="showAllProblems = !showAllProblems">
        {{ showAllProblems ? 'Show fewer' : 'Show all ' + plan.problems.length }}
      </button>
      }
    </div>
    }

    <!-- Changes -->
    @if (plan.updates.length) {
    <div class="rounded-xl border border-gray-100 overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            <th class="text-left px-4 py-2">Item</th>
            <th class="text-left px-3 py-2">Change</th>
          </tr>
        </thead>
        <tbody>
          @for (u of plan.updates; track u.id) {
          <tr class="border-t border-gray-50 align-top">
            <td class="px-4 py-2">
              <div class="font-semibold text-gray-900">{{ u.name }}</div>
              <div class="text-xs text-gray-500">{{ u.variant ? u.variant + ' · ' : '' }}{{ u.sku }}</div>
            </td>
            <td class="px-3 py-2">
              @for (c of u.changes; track c.field) {
              <div class="flex items-center gap-2 text-sm">
                <span class="text-gray-600 w-32 flex-shrink-0">{{ c.label }}@if (c.auto) { <span class="ml-1 text-[10px] font-semibold uppercase text-emerald-600" title="Follows the new price automatically">auto</span> }</span>
                <span class="text-gray-400 line-through tabular-nums">{{ fmt(c.from, c.money) }}</span>
                <mat-icon class="!text-[14px] !h-3.5 !w-3.5 text-gray-400">arrow_forward</mat-icon>
                <span class="font-bold tabular-nums" [class]="c.to !== null && c.from !== null && c.to < c.from ? 'text-red-700' : 'text-emerald-700'">{{ fmt(c.to, c.money) }}</span>
              </div>
              }
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    } @else if (!plan.problems.length) {
      <div class="text-center py-8 text-gray-500 text-sm">Nothing has changed in this sheet - every value matches what's in the system.</div>
    }
    }
  </div>

  <!-- Footer -->
  <div class="px-6 py-4 border-t border-gray-100 flex items-center gap-3 bg-gray-50/60">
    @if (applying) {
      <span class="inline-block h-4 w-4 rounded-full border-2 border-amber-500 border-t-transparent animate-spin"></span>
      <span class="text-sm text-gray-700">Updating {{ progress }} of {{ plan.updates.length }}…</span>
      <div class="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden"><div class="h-full bg-amber-500 transition-all" [style.width.%]="(progress / plan.updates.length) * 100"></div></div>
    } @else if (done) {
      <span class="flex-1"></span>
      <button class="px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-bold hover:bg-neutral-700" (click)="close()">Done</button>
    } @else {
      <span class="flex-1 text-xs text-gray-500">Nothing is changed until you press Update.</span>
      <button class="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100" (click)="close()">Cancel</button>
      <button class="px-5 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-bold hover:from-amber-300 hover:to-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
        [disabled]="!plan.updates.length" (click)="apply()">
        Update {{ plan.updates.length }} item{{ plan.updates.length === 1 ? '' : 's' }}
      </button>
    }
  </div>
</div>
